/**
 * Brokerage Hub — API client.
 *
 * Thin wrapper over the `/api/brokerage-hub/*` endpoints. Auth follows the
 * dominant CRM convention (a `token` query parameter), matching how the
 * Indirect Sales dashboard and Broker Master already call the backend.
 *
 * Every endpoint returns the `{ success, data }` envelope — `unwrap` peels
 * off `data` so callers work with the payload directly.
 */
import axios from 'axios';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/brokerage-hub`;

const unwrap = (res) => (res.data && 'data' in res.data ? res.data.data : res.data);

// ---------- Brokerages ----------

export const fetchDashboardSummary = (token) =>
  axios
    .get(`${BASE}/dashboard/summary`, { params: { token } })
    .then(unwrap);

export const fetchBrokerages = (token, filters = {}) =>
  axios
    .get(`${BASE}/brokerages`, {
      params: {
        token,
        search: filters.search || '',
        tier: filters.tier || 'all',
        status: filters.status || 'all',
        sort: filters.sort || 'converted',
      },
    })
    .then(unwrap);

export const fetchBrokerage = (token, id) =>
  axios.get(`${BASE}/brokerages/${id}`, { params: { token } }).then(unwrap);

export const createBrokerage = (token, payload) =>
  axios.post(`${BASE}/brokerages`, payload, { params: { token } }).then(unwrap);

export const updateBrokerage = (token, id, payload) =>
  axios.put(`${BASE}/brokerages/${id}`, payload, { params: { token } }).then(unwrap);

export const deleteBrokerage = (token, id) =>
  axios.delete(`${BASE}/brokerages/${id}`, { params: { token } }).then(unwrap);

export const fetchBrokerageActivity = (token, id) =>
  axios.get(`${BASE}/brokerages/${id}/activity`, { params: { token } }).then(unwrap);

export const uploadMou = (token, id, file) => {
  const form = new FormData();
  form.append('file', file);
  return axios
    .post(`${BASE}/brokerages/${id}/upload-mou`, form, {
      params: { token },
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(unwrap);
};

// ---------- Brokerage leads (broker_sales) ----------

export const fetchBrokerageLeads = (token, id, status = 'all') =>
  axios
    .get(`${BASE}/brokerages/${id}/leads`, { params: { token, status } })
    .then(unwrap);

export const createLead = (token, payload) =>
  axios.post(`${BASE}/leads`, payload, { params: { token } }).then(unwrap);

export const updateLead = (token, id, payload) =>
  axios.put(`${BASE}/leads/${id}`, payload, { params: { token } }).then(unwrap);

export const deleteLead = (token, id) =>
  axios.delete(`${BASE}/leads/${id}`, { params: { token } }).then(unwrap);

export const advanceLeadStatus = (token, id, status) =>
  axios
    .patch(`${BASE}/leads/${id}/advance-status`, status ? { status } : {}, {
      params: { token },
    })
    .then(unwrap);

// ---------- Bulk import ----------
//
// Migrate hundreds/thousands of brokerages from a spreadsheet. Each
// row goes through the same canonical dedup guard as the manual
// create endpoint so silent duplicates can't slip in. The response is
// a structured outcome the UI renders row-by-row.

export const bulkImportBrokerages = (token, file, { dryRun = false } = {}) => {
  const form = new FormData();
  form.append('file', file);
  return axios
    .post(`${BASE}/brokerages/bulk-import`, form, {
      params: { token, dry_run: dryRun },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,  // 2 min — enough for ~3k rows
    })
    .then(unwrap);
};

// Lives under /import-templates/ (not /brokerages/) so it doesn't
// shadow the GET /brokerages/{id} route on the backend. See the
// comment on the matching FastAPI handler for the full reason.
export const importTemplateXlsxUrl = (token) =>
  `${BASE}/import-templates/brokerages.xlsx?token=${encodeURIComponent(token)}`;

// ---------- Per-lead documents (UAE) ----------
//
// Each lead can carry the standard UAE paperwork (Emirates ID, passport,
// SPA, NOC, cheque copy, …). Storage lives on the same `broker_sales`
// row, so the Indirect Sales dashboard sees the same list automatically.

export const UAE_DOC_TYPES = [
  { value: 'emirates_id',        label: 'Emirates ID' },
  { value: 'passport',           label: 'Passport copy' },
  { value: 'visa',               label: 'UAE Residence Visa' },
  { value: 'trade_license',      label: 'Trade License' },
  { value: 'title_deed',         label: 'Title Deed / Oqood' },
  { value: 'noc',                label: 'NOC' },
  { value: 'ejari',              label: 'Ejari contract' },
  { value: 'spa',                label: 'SPA / MOU' },
  { value: 'booking_form',       label: 'Booking form' },
  { value: 'cheque',             label: 'Cheque copy' },
  { value: 'poa',                label: 'Power of Attorney' },
  { value: 'salary_certificate', label: 'Salary certificate' },
  { value: 'bank_statement',     label: 'Bank statement' },
  { value: 'passport_photo',     label: 'Passport photograph' },
  { value: 'other',              label: 'Other' },
];

export const labelForDocType = (value) =>
  (UAE_DOC_TYPES.find((t) => t.value === value) || {}).label || value;

export const fetchLeadDocuments = (token, leadId) =>
  axios
    .get(`${BASE}/leads/${leadId}/documents`, { params: { token } })
    .then(unwrap);

export const uploadLeadDocument = (token, leadId, file, docType, label = '') => {
  const form = new FormData();
  form.append('file', file);
  return axios
    .post(`${BASE}/leads/${leadId}/documents`, form, {
      params: { token, doc_type: docType, label },
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(unwrap);
};

export const deleteLeadDocument = (token, leadId, docId) =>
  axios
    .delete(`${BASE}/leads/${leadId}/documents/${docId}`, { params: { token } })
    .then(unwrap);

// Direct URL used by `<a download>` links — same auth pattern as the CSV
// exports below, with a streamed response on the backend.
export const leadDocumentDownloadUrl = (token, leadId, docId) =>
  `${BASE}/leads/${leadId}/documents/${docId}?token=${encodeURIComponent(token)}`;

// ---------- Indirect Sales metrics ----------
//
// Single round-trip that returns the KPI strip (total/active/visits/
// bookings/conversion) plus broker- and project-grouped breakdowns the
// dashboard can chart from.

export const fetchIndirectSalesMetrics = (token) =>
  axios
    .get(`${BASE}/indirect-sales/metrics`, { params: { token } })
    .then(unwrap);

// ---------- CSV export (direct download URLs) ----------

export const brokerageLeadsCsvUrl = (token, id) =>
  `${BASE}/brokerages/${id}/export-leads.csv?token=${encodeURIComponent(token)}`;

export const leaderboardCsvUrl = (token) =>
  `${BASE}/export/leaderboard.csv?token=${encodeURIComponent(token)}`;

// Excel (.xlsx) variants — same data, formatted workbook with bold
// header, frozen pane, AED currency formatting. Finance asked for
// these in the 05 Jun 2026 MoM (§11).
export const brokerageLeadsXlsxUrl = (token, id) =>
  `${BASE}/brokerages/${id}/export-leads.xlsx?token=${encodeURIComponent(token)}`;

export const leaderboardXlsxUrl = (token) =>
  `${BASE}/export/leaderboard.xlsx?token=${encodeURIComponent(token)}`;
